import Select from "react-select";
import SelectWithAdd from "../SelectWithAdd";
import Button from "../Button";
import { MdDeleteForever } from "react-icons/md";

const InputField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  unit,
  required,
  error,
  options = [],
  onAddNewClick,
  disabled = false,
  readOnly = false,
  accept = "",
  icon: Icon = null,
  iconPosition = "left",
  iconBg = "",
  iconColor = "text-gray-400",
  iconOffset = "",
  iconPadding = "",
  gridCols = "",
  bgColor = "",
  currentFileName = null,
  dieId = null,
  onFileRemove = null,
  previewUrl = "",
  checked,
  className,
  onBlur,
}) => {
  const defaultPadding = iconPadding
    ? iconPadding
    : iconPosition === "right"
      ? "pl-3 pr-10"
      : "pl-3 pr-3";

  const commonInputClass = `w-full ${defaultPadding} py-2 border border-gray-200 shadow-none text-[13px] focus:outline-none focus:border-primary transition-colors rounded-none ${error ? "border-red-500" : "border-gray-200"
    } ${readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"} ${bgColor}`;

  const renderFileInput = () => (
    <div className="flex flex-col gap-1">
      <div
        className={`border border-dashed p-3 flex items-center justify-center relative text-center cursor-pointer transition-colors duration-200 ${disabled
            ? "bg-gray-100 text-gray-400"
            : "bg-white hover:border-primary hover:bg-gray-50"
          } ${error ? "border-red-500" : "border-gray-300"}`}
      >
        <label className="w-full h-full flex flex-col items-center justify-center space-y-1 cursor-pointer">
          {type === "image" && previewUrl ? (
            <img
              src={
                previewUrl instanceof File
                  ? URL.createObjectURL(previewUrl)
                  : previewUrl
              }
              alt="Preview"
              className="max-h-32 object-contain"
            />
          ) : (
            <>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {type === "pdf"
                  ? "Upload PDF Drawing"
                  : "Upload Image"}
              </span>
              <span className="text-[10px] text-gray-400">
                {type === "pdf"
                  ? "PDF files only"
                  : "JPG, PNG, WEBP only"}
              </span>
            </>
          )}
          <input
            type="file"
            accept={type === "pdf" ? "application/pdf" : "image/*"}
            onChange={(e) => onChange(e.target.files?.[0])}
            disabled={disabled}
            className="hidden"
          />
        </label>
      </div>

      {currentFileName && (
        <div className="flex justify-between items-center text-[11px] text-gray-500 mt-0.5 bg-gray-50 p-1 border border-gray-200">
          <span className="truncate max-w-[150px]">{currentFileName}</span>
          {dieId && onFileRemove && (
            <button
              type="button"
              className="text-red-500 hover:text-red-700 font-bold"
              onClick={() => onFileRemove(dieId)}
            >
              REMOVE
            </button>
          )}
        </div>
      )}

      {error && <span className="text-[10px] text-red-500 font-medium">{error}</span>}
    </div>
  );

  return (
    <div className={`flex flex-col relative ${gridCols}`}>
      {label && (
        <div className="flex justify-between items-center mb-1">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5 relative group/tooltip">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        </div>
      )}

      {type === "select" ? (
        <Select
          options={options}
          label={label}
          value={options.find((opt) => opt.value === value) || null}
          onChange={(selected) => onChange(selected?.value || "")}
          placeholder={placeholder || `Select...`}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            control: (base) => ({
              ...base,
              minHeight: '36px',
              fontSize: '13px',
              borderRadius: '0',
              borderColor: error ? '#ef4444' : '#e2e8f0',
              boxShadow: 'none',
              '&:hover': {
                borderColor: '#0F3D75'
              }
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />

      ) : type === "selectWithAdd" ? (
        <SelectWithAdd
          value={value}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          error={error}
          required={required}
          onAddNewClick={onAddNewClick}
        />
      ) : type === "textarea" ? (
        <textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          className={`resize-none ${commonInputClass} ${className || ""}`}
          rows={3}
        />
      ) : type === "checkbox" ? (
        options.length > 0 ? (
          <div className="flex flex-col gap-2">
            {options.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={value?.includes(opt.value)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    let newValue = [...(value || [])];
                    if (checked) {
                      newValue.push(opt.value);
                    } else {
                      newValue = newValue.filter((v) => v !== opt.value);
                    }
                    onChange(newValue);
                  }}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={checked ?? value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
            <span className="text-sm text-slate-700">
              {placeholder || label}
            </span>
          </div>
        )
      ) : type === "radio" ? (
        <div className="flex gap-4">
          {options.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2">
              <input
                type="radio"
                name={label}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      ) : type === "pdf" || type === "image" ? (
        renderFileInput()
      ) : (
        <div className="relative flex items-center">
          {Icon && (
            <span
              className={`absolute ${iconOffset ||
                (iconPosition === "right"
                  ? "right-3 top-2.5"
                  : "left-3 top-2.5")
                } ${iconColor} ${iconBg} p-1 rounded`}
            >
              <Icon size={16} />
            </span>
          )}
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            className={`${commonInputClass} ${unit ? "pr-12" : ""}`}
          />
          {unit && (
            <span className="absolute right-3 pointer-events-none text-[11px] font-semibold text-slate-600 select-none bg-white px-1">
              {unit}
            </span>
          )}
        </div>
      )}

      {error && type !== "pdf" && type !== "image" && (
        <span className="text-xs text-red-500 mt-1">{error}</span>
      )}
    </div>
  );
};

export default InputField;
